import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Roles } from 'ngx/go-modules/src/enums';
import { NgxGroupService } from 'ngx/go-modules/src/services/group/group.service';
import { defaultSeparator, multiValueValidate } from 'ngx/go-modules/src/validators/multi-value/multi-value.validator';
import { BehaviorSubject, catchError, combineLatest, finalize, map, Observable, tap, EMPTY, startWith, distinctUntilChanged } from 'rxjs';
import { ConfirmDialogComponent, ConfirmDialogData } from '../confirm-dialog/confirm-dialog.component';
import { SeatOveragePolicies } from 'ngx/go-modules/src/enums/salesforce-license';
import { GoToastStatusType } from 'ngx/go-modules/src/enums/go-toast-status-type';
import { GoToastOptions } from 'ngx/go-modules/src/interfaces/go-toast-options';
import { NgxGoToastService } from 'ngx/go-modules/src/services/go-toast/go-toast.service';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import { groupToken } from 'go-modules/models/group-dep/group.factory';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { NgxLicenseService } from 'ngx/go-modules/src/services/license';
import { LazyPaymentLoader, lazyPaymentLoaderToken } from 'go-modules/payment-panel/lazy-payment-loader.service';
import { LICENSE_TRANSACTION_TYPE, PAYMENT_TYPES, PURCHASE_TYPES } from 'go-modules/payment-panel/payment-panel.controller';
import { noop } from 'angular';
import { UserRoleLocalizationUtil } from 'ngx/go-modules/src/utilities/user-role-localization/user-role-localization.util';
import { Group, LinkData } from 'ngx/go-modules/src/interfaces/groups/group.interface';

export interface InviteUsersDialogData {
	group: Group;
}

export interface Role {
	key: string;
	title: string;
	description: string;
};

@Component({
	selector: 'ngx-invite-users-dialog',
	template: require('./invite-users-dialog.component.html'),
	styles: [require('./invite-users-dialog.component.scss')],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class InviteUsersDialogComponent implements OnInit {
	public isLoading$ = new BehaviorSubject(true);
	public groupSubject = new BehaviorSubject(null);
	public group$: Observable<Group> = this.groupSubject.asObservable();
	public hasLtiIntegration = false;

	public sfLicense = null;
	public roles: Role[];
	public selectedRole: Role;
	public hasGroupLink = false;
	public sentInvites = [];
	public seatUsageSuccessLevel = 25;
	public seatUsageWarningLevel = 10;

	public form = new FormGroup({
		emails: new FormControl(null, [multiValueValidate(Validators.email)]),
		role: new FormControl(null, [Validators.required]),
		link: new FormControl(null)
	});

	public showSubmitButton$: Observable<boolean> = this.form.get('emails').valueChanges.pipe(
		map((emails) => emails?.length > 0),
		startWith(false),
		distinctUntilChanged()
	);

	constructor (
		private dialogRef: MatDialogRef<InviteUsersDialogComponent>,
		public translate: TranslateService,
		private groupService: NgxGroupService,
		private ngxGoToastService: NgxGoToastService,
		private dialog: MatDialog,
		private licenseService: NgxLicenseService,
		@Inject(selectedServiceToken) private selectedService: SelectedService,
		@Inject(userServiceToken) private userService: UserService,
		@Inject(groupToken) private Group,
		@Inject(lazyPaymentLoaderToken) private paymentLoader: LazyPaymentLoader,
		@Inject(MAT_DIALOG_DATA) private data: InviteUsersDialogData
	) {}

	// A small helper to open the dialog with all the correct typing
	public static open (
		dialog: MatDialog,
		config?: MatDialogConfig<InviteUsersDialogData>
	): MatDialogRef<InviteUsersDialogComponent, boolean> {
		return dialog.open<InviteUsersDialogComponent, InviteUsersDialogData>
		(InviteUsersDialogComponent, config);
	}

	public ngOnInit () {
		this.data.group.hasLtiIntegration().$promise
			.then((details) => this.hasLtiIntegration = details.has_lti_integration)
			.catch(() => noop);

		if (this.isCourse()) {
			combineLatest([
				this.groupService.getShareLink(this.data.group.group_id),
				this.groupService.getCourseUsersList(this.data.group.group_id)
			]).pipe(
				map(([linkData, usersList]) => ({
					...this.data.group,
					...linkData,
					usersCount: usersList.length
				}))
			).subscribe((group) => {
				this.groupSubject.next(group);
				this.form.get('link').setValue(group.link);
			});

			// If course is not using product, then check license seats availability
			if (!this.data.group.product_id) {
				// get license for most current seats remaining
				// also need full license here to pass to the pay form
				this.groupService.getLicense(this.data.group.group_id).pipe(
					tap((license: any) => {
						if (license?.salesforce_license.seat_overage_policy === SeatOveragePolicies.RESTRICTED) {
							const invertedLicense = license.salesforce_license;
							invertedLicense.license = Object.assign({}, license, {salesforce_license: undefined});

							this.sfLicense = invertedLicense;
							this.showSeatsAvailabilityToast();
						}
					})
				).subscribe();
			}
		} else {
			this.groupSubject.next(this.data.group);
		}

		this.constructRoles();
		const groupRole = this.userService.currentUser.is_root_user ? this.Group.role.OWNER : this.data.group.role;
		this.roles = this.roles.filter((role) => {
			return this.Group.rolePrecedence[role.key] >= this.Group.rolePrecedence[groupRole] &&
				role.key !== this.Group.role.OWNER;
		});

		this.selectedRole = this.roles[this.roles.length - 1];
		this.form.get('role').setValue(this.selectedRole);
	}

	public handleSendInvite (): void {
		const group = this.groupSubject.getValue();

		if (this.form.invalid) {
			return;
		}

		this.form.controls.emails.value
			.split(defaultSeparator)
			.filter((email) => email !== '') // Remove leading/trailing separators resulting in empty values
			.filter((v, i, a) => a.indexOf(v) === i); // Remove duplicate values

		if ([this.Group.role.ADMIN, this.Group.role.INSTRUCTOR].includes(this.form.controls.role.value.key)) {
			const localizedGroupType = this.localizeGroupType(group.type);

			this.dialog.open(ConfirmDialogComponent, {
				data: {
					title: this.translate.instant('modal-invite-users_controller-invite-confirm-title', {
						role: (this.translate.instant(`modal-invite-users_controller-invite-confirm-role-${this.form.controls.role.value.key}`)) // to capitalize
					}),
					message: this.translate.instant('modal-invite-users_controller-invite-confirm-text', {
						role: (this.translate.instant(`modal-invite-users_controller-invite-confirm-role-${this.form.controls.role.value.key}`)), // to capitalize
						group: localizedGroupType
					}),
					confirmText: this.translate.instant('modal-invite-users_controller-invite-confirm-yes', {
						role: (this.translate.instant(`modal-invite-users_controller-invite-confirm-role-${this.form.controls.role.value.key}`)).toUpperCase()
					}),
					cancelText: this.translate.instant('common_cancel')
				} as ConfirmDialogData
			})
				.afterClosed()
				.subscribe((confirm) => confirm && this.sendInvites());
		} else {
			this.sendInvites();
		}
	}

	public getInvalidEmails (): string {
		return this.form.controls.emails.getError('multi')
			.map((error) => error.value)
			.join(', ');
	}

	public openNewJoinLinkModal () {
		if (!this.groupSubject.getValue().uuid) {
			this.createShareLink();
			return;
		}

		const dialogRef: MatDialogRef<ConfirmDialogComponent, any> = this.dialog.open(ConfirmDialogComponent, {
			data: {
				title: this.translate.instant('modal-new-join-link_new-link'),
				message: this.translate.instant('modal-new-join-link_sure-create-link')
			} as ConfirmDialogData
		});

		dialogRef.afterClosed().subscribe((confirm) => confirm && this.createShareLink());
	}

	public isCourse (): boolean {
		return this.data.group.isCourse();
	}

	public close () {
		this.dialogRef.close(this.sentInvites);
	}

	private constructRoles (): void {
		this.roles = [
			{
				key: Roles.ADMIN,
				title: this.translate.instant(Roles.ADMIN),
				description: this.translate.instant('modal-invite_role-item-description-admin')
			},
			{
				key: Roles.INSTRUCTOR,
				title: this.translate.instant(Roles.INSTRUCTOR),
				description: this.translate.instant('modal-invite_role-item-description-instructor')
			}
		];

		if (this.isCourse()) {
			this.roles.push({
				key: Roles.REVIEWER,
				title: this.translate.instant(Roles.REVIEWER),
				description: this.translate.instant('modal-invite_role-item-description-reviewer')
			});
			this.roles.push({
				key: Roles.PRESENTER,
				title: this.translate.instant(Roles.PRESENTER),
				description: this.translate.instant('modal-invite_role-item-description-presenter')
			});
		}
	}

	private createShareLink (): void {
		const group = this.groupSubject.getValue();

		this.groupService.createShareLink(group.group_id).subscribe((linkData: LinkData) => {
			const splitUrl = linkData.link.split('/');
			const extraData = { uuid: splitUrl[splitUrl.length - 1], link: linkData.link };
			this.groupSubject.next({...group, ...extraData});
			this.form.get('link').setValue(linkData.link);
		});
	}

	private getSeatsAvailablePercent (): number {
		return 100 - ((this.sfLicense.total_seats_consumed / this.sfLicense.total_seats) * 100);
	};

	private purchaseSeats = async () => {
		const sfLicense = this.sfLicense;

		// dismiss invite user modal
		this.dialogRef.close();

		// open payment sidepanel
		this.licenseService.fetchOrCreateUserAccount(
			sfLicense.license_id,
			this.selectedService.getOrg().group_id
		).subscribe((account)=> {
			this.paymentLoader.openPayForm(
				this.userService.currentUser,
				account,
				() => {},
				sfLicense,
				PAYMENT_TYPES.CARD,
				PURCHASE_TYPES.LICENSE,
				LICENSE_TRANSACTION_TYPE.SEATS
			);
		});
	};

	private showSeatsAvailabilityToast (): void {
		const sfLicense = this.sfLicense;
		const seatsAvailablePercent = this.getSeatsAvailablePercent();
		let toastType: string = '';
		let toastMessage: string = '';

		if (seatsAvailablePercent > this.seatUsageSuccessLevel) {
			toastType = GoToastStatusType.SUCCESS;
		} else if (seatsAvailablePercent >= this.seatUsageWarningLevel) {
			toastType = GoToastStatusType.WARNING;
		} else {
			toastType = GoToastStatusType.ERROR;
		}

		const toastDetails: GoToastOptions = {
			type: toastType,
			hideToastTime: 10000,
			message: ''
		};

		if (sfLicense.is_license_admin) {
			toastMessage = 'modal-invite-user_seats-toast-message-admin';
			toastDetails.actionLink = {
				title: this.translate.instant('modal-invite-user_seats-toast-message-admin-purchase-seats'),
				action: this.purchaseSeats
			};
		} else {
			toastMessage = 'modal-invite-user_seats-toast-message-non-admin';
		}

		toastDetails.message = this.translate.instant(toastMessage, {
			seatCount: sfLicense.total_seats - sfLicense.total_seats_consumed
		});

		this.ngxGoToastService.createToast(toastDetails);
	}

	private sendInvites (): void {
		const group = this.groupSubject.getValue();
		const emails = this.form.controls.emails.value
			.split(defaultSeparator)
			.map((email) => ({email, role: this.form.controls.role.value.key}));

		this.groupService.invite(group.group_id, emails).pipe(
			map((results) => {
				this.ngxGoToastService.createToast({
					type: GoToastStatusType.SUCCESS,
					message: 'invite-sent'
				});

				results.forEach((result) => {
					this.sentInvites.push({
						inviteId: result.invite_id,
						uuid: result.uuid,
						groupId: result.group_id,
						userId: result.user_id,
						email: result.email,
						role: result.role,
						role_localized: UserRoleLocalizationUtil.localize(result.role),
						lastSent: new Date()
					});
				});
			}),
			catchError(() => {
				this.ngxGoToastService.createToast({
					type: GoToastStatusType.ERROR,
					message: 'common-generic-error-message'
				});
				return EMPTY;
			}),
			finalize(() => {
				this.form.controls.emails.reset();
			})
		).subscribe();
	}

	private localizeGroupType (groupType: string): string {
		const translationMap = {
			[this.Group.COURSE]: 'models-group_factory-type-course',
			[this.Group.ACCOUNT]: 'models-group_factory-type-account',
			[this.Group.ORG]: 'models-group_factory-type-org'
		};

		return this.translate.instant(translationMap[groupType]);
	}
}
